import React from "react";
import { api, formatPHTDateTime } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Inbox,
  CheckCircle2,
  X,
  Phone,
  Mail,
  MessageSquare,
  CalendarPlus,
  RefreshCw,
  Trash2,
} from "lucide-react";

const STATUS_META = {
  new: { label: "New", className: "bg-blue-500/20 text-blue-200 border-blue-500/40" },
  contacted: { label: "Contacted", className: "bg-amber-500/20 text-amber-200 border-amber-500/40" },
  converted: { label: "Converted", className: "bg-emerald-500/20 text-emerald-200 border-emerald-500/40" },
  dismissed: { label: "Dismissed", className: "bg-slate-500/20 text-slate-300 border-slate-500/40" },
};

const FILTERS = ["new", "contacted", "converted", "dismissed", "all"];

export default function AdminInbox() {
  const [leads, setLeads] = React.useState([]);
  const [stats, setStats] = React.useState({ new: 0, contacted: 0, converted: 0, dismissed: 0, total: 0 });
  const [filter, setFilter] = React.useState("new");
  const [loading, setLoading] = React.useState(true);
  const [convertLead, setConvertLead] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        api.get(`/admin/leads?status=${filter}`),
        api.get("/admin/leads/stats"),
      ]);
      setLeads(l.data || []);
      setStats(s.data || {});
    } catch (e) {
      toast.error("Failed to load inbox");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  React.useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch {
      toast.error("Failed to update");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      toast.success("Lead deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-inbox">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="overline mb-1">Customer Conversations · PHT</p>
          <h1 className="font-serif-display text-3xl md:text-4xl flex items-center gap-3">
            <Inbox size={26} className="text-[#C5A059]" /> Inbox
          </h1>
          <p className="text-[#A1A4B5] text-sm mt-2 max-w-2xl">
            Leads sent from Facebook Messenger, ManyChat, or any AI agent calling{" "}
            <span className="font-mono text-[#C5A059]">POST /api/public/messenger-webhook</span>.
            Triage, then convert with one click into a confirmed Overnight booking.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={load}
          className="bg-transparent border-[var(--border)]"
          data-testid="inbox-refresh"
        >
          <RefreshCw size={14} className={loading ? "mr-2 animate-spin" : "mr-2"} /> Refresh
        </Button>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {["new", "contacted", "converted", "dismissed", "total"].map((k) => (
          <button
            key={k}
            onClick={() => k !== "total" && setFilter(k)}
            data-testid={`inbox-stat-${k}`}
            className={`text-left rounded-xl border p-4 transition-all ${
              filter === k ? "border-[#C5A059] bg-[#C5A059]/10" : "border-[var(--border)] hover:border-[#C5A059]/40"
            }`}
          >
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#A1A4B5]">{k}</div>
            <div className="font-serif-display text-3xl mt-1">{stats[k] ?? 0}</div>
          </button>
        ))}
      </div>

      {/* Filter pills */}
      <div className="flex items-center gap-2 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`inbox-filter-${f}`}
            className={`px-3 py-1.5 text-xs uppercase tracking-[0.18em] rounded-full border transition-all ${
              filter === f
                ? "border-[#C5A059] text-[#C5A059] bg-[#C5A059]/10"
                : "border-[var(--border)] text-[#A1A4B5] hover:text-white"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Empty / Loading */}
      {loading ? (
        <p className="text-[#A1A4B5] text-sm">Loading conversations…</p>
      ) : leads.length === 0 ? (
        <div
          className="rounded-2xl border border-dashed border-[var(--border)] p-10 text-center text-[#A1A4B5]"
          data-testid="inbox-empty"
        >
          <MessageSquare size={32} className="mx-auto mb-3 text-[#A1A4B5]/60" />
          <p className="font-serif-display text-xl text-white">No {filter === "all" ? "" : filter} conversations yet</p>
          <p className="text-sm mt-2 max-w-md mx-auto">
            Point your ManyChat / Make.com / Messenger bot at{" "}
            <span className="font-mono text-[#C5A059]">POST /api/public/messenger-webhook</span>{" "}
            with header <span className="font-mono text-[#C5A059]">X-API-Key</span> to start receiving leads here.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {leads.map((l) => (
            <LeadCard
              key={l.id}
              lead={l}
              onUpdate={updateStatus}
              onDelete={remove}
              onConvert={() => setConvertLead(l)}
            />
          ))}
        </div>
      )}

      <ConvertLeadDialog
        lead={convertLead}
        onClose={() => setConvertLead(null)}
        onConverted={() => {
          setConvertLead(null);
          load();
        }}
      />
    </div>
  );
}

function LeadCard({ lead, onUpdate, onDelete, onConvert }) {
  const meta = STATUS_META[lead.status] || STATUS_META.new;
  const isConverted = lead.status === "converted";
  const isDismissed = lead.status === "dismissed";

  return (
    <div
      className="rounded-2xl border border-[var(--border)] bg-[#13151D] p-5"
      data-testid={`lead-card-${lead.id}`}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-serif-display text-xl truncate" data-testid="lead-name">
              {lead.name || <span className="italic text-[#A1A4B5]">Anonymous</span>}
            </h3>
            <Badge className={`${meta.className} border text-[10px] uppercase tracking-widest`}>
              {meta.label}
            </Badge>
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#A1A4B5] border border-[var(--border)] px-2 py-0.5 rounded-full">
              {lead.source}
            </span>
          </div>
          {lead.contact && (
            <div className="text-sm text-[#A1A4B5] mt-2 flex items-center gap-2">
              {lead.contact.includes("@") ? <Mail size={13} /> : <Phone size={13} />}
              <span className="text-white">{lead.contact}</span>
            </div>
          )}
          {lead.message && (
            <blockquote className="mt-3 text-sm border-l-2 border-[#C5A059]/50 pl-3 text-[#E5E7EB] whitespace-pre-wrap">
              {lead.message}
            </blockquote>
          )}
          <div className="mt-3 flex items-center gap-4 text-[11px] text-[#A1A4B5] flex-wrap">
            {lead.requested_date && (
              <span>
                Requested date: <span className="text-white">{lead.requested_date}</span>
              </span>
            )}
            {lead.requested_guests > 0 && (
              <span>
                Guests: <span className="text-white">{lead.requested_guests}</span>
              </span>
            )}
            <span>Received {formatPHTDateTime(lead.created_at)}</span>
            {lead.admin_notes && (
              <span className="italic">Notes: {lead.admin_notes}</span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 justify-end">
          {!isConverted && !isDismissed && lead.status === "new" && (
            <Button
              size="sm"
              variant="outline"
              className="bg-transparent border-amber-400/40 text-amber-200 hover:bg-amber-500/10"
              onClick={() => onUpdate(lead.id, "contacted")}
              data-testid={`lead-mark-contacted-${lead.id}`}
            >
              <CheckCircle2 size={14} className="mr-1" /> Mark Contacted
            </Button>
          )}
          {!isConverted && (
            <Button
              size="sm"
              className="gold-btn rounded-full"
              onClick={onConvert}
              data-testid={`lead-convert-${lead.id}`}
            >
              <CalendarPlus size={14} className="mr-1" /> Convert to Booking
            </Button>
          )}
          {!isDismissed && !isConverted && (
            <Button
              size="sm"
              variant="outline"
              className="bg-transparent border-[var(--border)] text-[#A1A4B5] hover:text-white"
              onClick={() => onUpdate(lead.id, "dismissed")}
              data-testid={`lead-dismiss-${lead.id}`}
            >
              <X size={14} className="mr-1" /> Dismiss
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="text-[#A1A4B5] hover:text-red-300"
            onClick={() => onDelete(lead.id)}
            data-testid={`lead-delete-${lead.id}`}
            title="Delete permanently"
          >
            <Trash2 size={14} />
          </Button>
        </div>
      </div>
      {isConverted && lead.converted_booking_id && (
        <p className="mt-3 text-[11px] text-emerald-300">
          ✓ Converted to booking #{lead.converted_booking_id.slice(0, 8)}
        </p>
      )}
    </div>
  );
}

function ConvertLeadDialog({ lead, onClose, onConverted }) {
  const [form, setForm] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!lead) return;
    api.get("/rates").then((r) => {
      const ov = (r.data || []).find((x) => x.type === "whole_day");
      // Smart-detect whether lead.contact is an email or a phone
      const contact = lead.contact || "";
      const isEmail = contact.includes("@");
      setForm({
        guest_name: lead.name || "",
        guest_email: isEmail ? contact : "",
        guest_phone: isEmail ? "" : contact,
        num_guests: lead.requested_guests || 1,
        booking_date: lead.requested_date || new Date().toISOString().slice(0, 10),
        num_days: 1,
        total_price: ov?.price || 24000,
        downpayment_amount: Math.round((ov?.price || 24000) * 0.5),
        special_requests: lead.message || "",
      });
    });
  }, [lead]);

  const submit = async () => {
    if (!form?.guest_name?.trim()) return toast.error("Guest name is required");
    if (!form?.booking_date) return toast.error("Booking date is required");
    setSubmitting(true);
    try {
      await api.post(`/admin/leads/${lead.id}/convert`, {
        ...form,
        num_guests: Number(form.num_guests) || 1,
        num_days: Number(form.num_days) || 1,
        total_price: Number(form.total_price) || 0,
        downpayment_amount: Number(form.downpayment_amount) || 0,
        send_email: false,
      });
      toast.success("Lead converted to confirmed booking");
      onConverted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Conversion failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!lead} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0A0B10] border-[var(--border)] max-w-xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">Convert Lead → Booking</DialogTitle>
          <DialogDescription className="text-[#A1A4B5] text-xs">
            Creates a <b>confirmed</b> Overnight Use booking. The lead will be marked as <b>converted</b>.
          </DialogDescription>
        </DialogHeader>
        {form && (
          <div className="space-y-4 mt-2">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Guest Name" testid="convert-name">
                <Input
                  value={form.guest_name}
                  onChange={(e) => setForm({ ...form, guest_name: e.target.value })}
                  className="bg-[#13151D] border-[var(--border)]"
                  data-testid="convert-name-input"
                />
              </Field>
              <Field label="Phone" testid="convert-phone">
                <Input
                  value={form.guest_phone}
                  onChange={(e) => setForm({ ...form, guest_phone: e.target.value })}
                  className="bg-[#13151D] border-[var(--border)]"
                  data-testid="convert-phone-input"
                />
              </Field>
              <Field label="Email (optional)" testid="convert-email">
                <Input
                  type="email"
                  value={form.guest_email}
                  onChange={(e) => setForm({ ...form, guest_email: e.target.value })}
                  className="bg-[#13151D] border-[var(--border)]"
                  data-testid="convert-email-input"
                />
              </Field>
              <Field label="Guests" testid="convert-guests">
                <Input
                  type="number"
                  min="1"
                  value={form.num_guests}
                  onChange={(e) => setForm({ ...form, num_guests: e.target.value })}
                  className="bg-[#13151D] border-[var(--border)]"
                  data-testid="convert-guests-input"
                />
              </Field>
              <Field label="Booking Date" testid="convert-date">
                <Input
                  type="date"
                  value={form.booking_date}
                  onChange={(e) => setForm({ ...form, booking_date: e.target.value })}
                  className="bg-[#13151D] border-[var(--border)]"
                  data-testid="convert-date-input"
                />
              </Field>
              <Field label="Length (days)" testid="convert-days">
                <Input
                  type="number"
                  min="1"
                  value={form.num_days}
                  onChange={(e) => setForm({ ...form, num_days: e.target.value })}
                  className="bg-[#13151D] border-[var(--border)]"
                  data-testid="convert-days-input"
                />
              </Field>
              <Field label="Total Price (₱)" testid="convert-price">
                <Input
                  type="number"
                  min="0"
                  value={form.total_price}
                  onChange={(e) => setForm({ ...form, total_price: e.target.value })}
                  className="bg-[#13151D] border-[var(--border)]"
                  data-testid="convert-price-input"
                />
              </Field>
              <Field label="Downpayment (₱)" testid="convert-downpayment">
                <Input
                  type="number"
                  min="0"
                  value={form.downpayment_amount}
                  onChange={(e) => setForm({ ...form, downpayment_amount: e.target.value })}
                  className="bg-[#13151D] border-[var(--border)]"
                  data-testid="convert-downpayment-input"
                />
              </Field>
            </div>
            <Field label="Special Requests / Notes" testid="convert-notes">
              <Textarea
                rows={3}
                value={form.special_requests}
                onChange={(e) => setForm({ ...form, special_requests: e.target.value })}
                className="bg-[#13151D] border-[var(--border)]"
                data-testid="convert-notes-input"
              />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="bg-transparent border-[var(--border)]"
                data-testid="convert-cancel"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={submit}
                disabled={submitting}
                className="gold-btn rounded-full px-6"
                data-testid="convert-submit"
              >
                {submitting ? "Converting…" : "Confirm Booking"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
